import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/lib/monitoring';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the real error so it's discoverable in logs / network panel
    // and forward it to crash reporting.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught error:', error, info);
    reportError(error, { source: 'ErrorBoundary', componentStack: info.componentStack ?? undefined });
  }

  handleReset = () => {
    this.setState({ error: null });
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const message = this.state.error.message || 'Unknown error';

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
        <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-lg">
          <h1 className="text-lg font-extrabold uppercase tracking-widest text-foreground">
            Something went wrong
          </h1>
          <p className="mt-3 text-sm text-muted-foreground break-words">
            {message}
          </p>
          <button
            onClick={this.handleReset}
            className="mt-6 w-full rounded-full bg-primary px-4 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground press-scale"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
